// Sport Roles API Helper
// This file provides helper functions for working with the sport roles API

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

export const sportRolesAPI = {
  // Get all supported sports
  getSports: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/sport-roles/sports`);
      const data = await response.json();
      return data.success ? data.sports : [];
    } catch (error) {
      console.error('Error fetching sports:', error);
      return [];
    }
  },

  // Get roles for a specific sport and type
  getRoles: async (sport, roleType = 'player') => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/sport-roles/sport/${sport}/${roleType}`);
      const data = await response.json();
      return data.success ? data.roles : [];
    } catch (error) {
      console.error('Error fetching roles:', error);
      return [];
    }
  },

  // Get all roles for a sport (both player and coach)
  getAllRoles: async (sport) => {
    try {
      const [playerRoles, coachRoles] = await Promise.all([
        sportRolesAPI.getRoles(sport, 'player'),
        sportRolesAPI.getRoles(sport, 'coach')
      ]);
      return {
        players: playerRoles,
        coaches: coachRoles
      };
    } catch (error) {
      console.error('Error fetching all roles:', error);
      return { players: [], coaches: [] };
    }
  },

  // Get role details by key
  getRoleDetails: async (sport, roleKey) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/sport-roles/sport/${sport}/role/${roleKey}`);
      const data = await response.json();
      return data.success ? data.role : null;
    } catch (error) {
      console.error('Error fetching role details:', error);
      return null;
    }
  }
};

export const teamAPI = {
  // Get team role summary
  getRoleSummary: async (teamId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/teams/${teamId}/role-summary`);
      const data = await response.json();
      return data.success ? data : null;
    } catch (error) {
      console.error('Error fetching team role summary:', error);
      return null;
    }
  },

  // Validate team composition
  validateTeam: async (teamId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/teams/${teamId}/validation`);
      const data = await response.json();
      return data.success ? data : null;
    } catch (error) {
      console.error('Error validating team:', error);
      return null;
    }
  },

  // Assign role to player
  assignRole: async (teamId, playerId, roleData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/teams/${teamId}/players/${playerId}/role`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(roleData)
      });
      const data = await response.json();
      return data.success ? data : null;
    } catch (error) {
      console.error('Error assigning role:', error);
      return null;
    }
  },

  // Remove role from player
  removeRole: async (teamId, playerId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/teams/${teamId}/players/${playerId}/role`, {
        method: 'DELETE'
      });
      const data = await response.json();
      return data.success ? data : null;
    } catch (error) {
      console.error('Error removing role:', error);
      return null;
    }
  },

  // Get available roles for team
  getAvailableRoles: async (teamId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/teams/${teamId}/roles`);
      const data = await response.json();
      return data.success ? data.roles : [];
    } catch (error) {
      console.error('Error fetching available roles:', error);
      return [];
    }
  }
};

export default {
  sportRolesAPI,
  teamAPI
};
